import type { ContentAccess } from "./content";
import type { Rng } from "./rng";
import type { ErrorCode, GameId, GameResultSummary, Lang, RoomCore } from "./types";

export interface EngineCtx {
  now: number;
  rng: Rng;
  content: ContentAccess;
}

/** Every engine state carries a single deadline so the host can pause/shift it. */
export interface TimedState {
  deadline: number | null; // epoch ms when tick() must run, null = waiting on players
  phaseMs: number; // total length of the current timed phase (for progress bars)
}

export interface BotPlan {
  key: string; // stable while the plan is valid (e.g. "write:3")
  delay: number; // ms from now
  action: unknown;
}

export interface Engine<S extends TimedState = any, Settings = any, View = any> {
  id: GameId;
  minPlayers: number;
  maxPlayers: number;
  supportsBots: boolean;
  defaults(lang: Lang): Settings;
  /** Clamp/validate untrusted settings, falling back to `prev`. */
  sanitize(input: unknown, prev: Settings): Settings;
  /** Build the initial state when the host presses start. May return an error (e.g. no content). */
  start(room: RoomCore<Settings, S>, ctx: EngineCtx): S | ErrorCode;
  /** Apply a player's game action. Must validate before mutating. Return state or an error. */
  act(room: RoomCore<Settings, S>, s: S, actorId: string, action: any, ctx: EngineCtx): S | ErrorCode;
  /** The deadline passed. */
  tick(room: RoomCore<Settings, S>, s: S, ctx: EngineCtx): S;
  /** A player left or was kicked mid-game. */
  onLeave?(room: RoomCore<Settings, S>, s: S, playerId: string, ctx: EngineCtx): S;
  /** A player joined mid-game. */
  onJoin?(room: RoomCore<Settings, S>, s: S, playerId: string, ctx: EngineCtx): S;
  /** The host paused the room (engines with their own clocks freeze them here). */
  onPause?(room: RoomCore<Settings, S>, s: S, ctx: EngineCtx): S;
  /** The host resumed the room after `pausedMs`. Without this hook the deadline is shifted. */
  onResume?(room: RoomCore<Settings, S>, s: S, pausedMs: number, ctx: EngineCtx): S;
  /** Server-internal messages (e.g. the judge's verdict). Never reachable by players. */
  system?(room: RoomCore<Settings, S>, s: S, msg: any, ctx: EngineCtx): S;
  /** Projection for one viewer (null = public screen). Never leak secrets here. */
  view(room: RoomCore<Settings, S>, s: S, viewerId: string | null, now: number): View;
  bot?(room: RoomCore<Settings, S>, s: S, botId: string, ctx: EngineCtx): BotPlan | null;
  isOver(s: S): boolean;
  /** What to record when the game is over (null = nothing, e.g. a match that was called off). */
  summary?(room: RoomCore<Settings, S>, s: S): GameResultSummary | null;
}

export const isError = (x: unknown): x is ErrorCode => typeof x === "string";

/** Active (non-audience) players in join order. */
export function activePlayers(room: RoomCore) {
  return room.players.filter((p) => p.role === "player");
}

export function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

export function oneOf<T extends string | number>(v: unknown, options: readonly T[], fallback: T): T {
  return (options as readonly unknown[]).includes(v) ? (v as T) : fallback;
}

export function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === "boolean" ? v : fallback;
}

export function stringList(v: unknown, allowed: readonly string[], fallback: string[]): string[] {
  if (!Array.isArray(v)) return fallback;
  const out = v.filter((x): x is string => typeof x === "string" && allowed.includes(x));
  return Array.from(new Set(out));
}
